import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input } from '@/components/ui'
import { Heart, Copy, Check, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'

interface CryptoAddress {
  label: string
  network: string
  address: string
  color: string
}

const addresses: CryptoAddress[] = [
  {
    label: 'USDT',
    network: 'TRC20',
    address: 'TFyYEfWGJ6RoX9rCyzycBEufpN7gBpVuko',
    color: 'text-green-400',
  },
  {
    label: 'ETH',
    network: 'ERC20',
    address: '0x6d88e05ab287c74e5750ca0261651617fc6bff63',
    color: 'text-blue-400',
  },
  {
    label: 'BTC',
    network: 'BEP20',
    address: '1GPDcUfMv398y7sZyrGUqqfPM7oWw3ybMQ',
    color: 'text-orange-400',
  },
]

const BINANCE_ID = '55033920'
const TELEGRAM_LINK = 'https://t.me/paul_zyy?text=Hello%20I%20wish%20to%20donate%20to%20support%20and%20thank%20you%20for%20your%20work.'

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(value)
    setCopied(true)
    toast.success(`${label} copied to clipboard`)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="flex items-center gap-2">
      <Input value={value} readOnly className="font-mono text-xs" />
      <Button variant="outline" size="icon" className="shrink-0" onClick={handleCopy}>
        {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  )
}

export function Donate() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">
          <span className="text-primary glow-text">{'>'}</span> Donate
        </h1>
        <p className="text-muted-foreground">
          Support SCB|PHIL Terminal development. Every contribution helps keep the project alive and free.
        </p>
      </div>

      {/* Thank you banner */}
      <Card className="glass border-pink-500/30 bg-pink-500/5">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-pink-500/10 border border-pink-500/30">
              <Heart className="h-6 w-6 text-pink-500" />
            </div>
            <div>
              <h3 className="font-semibold">Thank you for considering a donation!</h3>
              <p className="text-sm text-muted-foreground">
                Your support helps cover server costs, development time, and new features.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Crypto addresses */}
        {addresses.map((crypto) => (
          <Card key={crypto.label} className="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className={crypto.color}>{crypto.label}</span>
                <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  {crypto.network}
                </span>
              </CardTitle>
              <CardDescription>Send {crypto.label} via {crypto.network} network</CardDescription>
            </CardHeader>
            <CardContent>
              <CopyField label={`${crypto.label} address`} value={crypto.address} />
            </CardContent>
          </Card>
        ))}

        {/* Binance ID */}
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="text-yellow-400">Binance</span>
              <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded">
                Pay ID
              </span>
            </CardTitle>
            <CardDescription>Send via Binance Pay using ID</CardDescription>
          </CardHeader>
          <CardContent>
            <CopyField label="Binance ID" value={BINANCE_ID} />
          </CardContent>
        </Card>
      </div>

      {/* Telegram / Other methods */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Other Payment Methods
          </CardTitle>
          <CardDescription>
            Want to donate using a different method? Reach out via Telegram.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <a href={TELEGRAM_LINK} target="_blank" rel="noopener noreferrer">
            <Button className="w-full sm:w-auto">
              <ExternalLink className="h-4 w-4 mr-2" />
              Contact via Telegram
            </Button>
          </a>
        </CardContent>
      </Card>
    </div>
  )
}
